<script setup lang="ts">
import { useForm } from '@formwerk/core'
import { DATE_OF_BIRTH_MIN } from '#shared/utils/dateOfBirth'
import { LeadSchema } from '#shared/utils/leadSchema'

interface BookFormProps {
  submitAlign?: 'center' | 'start'
  submitTheme?: 'primary' | 'scheme'
}

withDefaults(defineProps<BookFormProps>(), {
  submitAlign: 'center',
  submitTheme: 'primary',
})

const siteConfig = useSiteConfig()

const { formProps, handleSubmit, reset, isSubmitting } = useForm({
  schema: LeadSchema,
  disableHtmlValidation: true,
})

const status = ref<'idle' | 'success' | 'error'>('idle')
const errorDetail = ref<string>()

const today = new Date().toISOString().slice(0, 10)

const onSubmit = handleSubmit(async (data) => {
  status.value = 'idle'
  errorDetail.value = undefined

  try {
    await $fetch('/api/lead', { method: 'POST', body: data.toObject() })
    status.value = 'success'
    reset()
  }
  catch (error) {
    status.value = 'error'
    errorDetail.value = pabauErrorMessage(error)
  }
})

// formProps ships its own onSubmit that falls back to a native `form.submit()`;
// replace it so only our fetch runs.
const formAttrs = { ...formProps, onSubmit }
</script>

<template>
  <TransitionDefault mode="out-in">
    <p v-if="status === 'success'" class="typo-sans-1 text-center uppercase" role="status">
      {{ $t('form_book_success') }}
    </p>

    <form v-else v-bind="formAttrs" class="grid space-y-5">
      <div class="gap-x-5 grid space-y-5 sm:grid-cols-2 sm:space-y-0">
        <TextField :label="$t('first_name')" name="firstName" autocomplete="given-name" required />
        <TextField :label="$t('last_name')" name="lastName" autocomplete="family-name" required />
      </div>
      <DateField :label="$t('date_of_birth')" name="dateOfBirth" autocomplete="bday" :min="DATE_OF_BIRTH_MIN" :max="today" required />
      <TextField :label="$t('email')" name="email" type="email" autocomplete="email" required />
      <PhoneField :label="$t('mobile')" name="mobile" autocomplete="tel" required />
      <TextareaField :label="`${$t('what_brings_you')} ${siteConfig.name}`" name="message" required />
      <CheckboxField :label="$t('marketing_consent')" name="marketingConsent" />

      <!-- Honeypot, hidden from users and assistive tech -->
      <div class="sr-only" aria-hidden="true" inert>
        <TextField label="Company" name="company" autocomplete="off" />
      </div>

      <p v-if="status === 'error'" class="typo-sans-1 c-red text-center uppercase" role="alert">
        {{ errorDetail || $t('form_book_error') }}
      </p>

      <div class="mt-8 flex" :class="submitAlign === 'start' ? 'justify-start' : 'justify-center'">
        <Button
          :title="$t('send')"
          :theme="submitTheme"
          type="submit"
          :disabled="isSubmitting"
          :class="{ 'opacity-50': isSubmitting }"
        />
      </div>
    </form>
  </TransitionDefault>
</template>
